import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isObject, toLower, trim } from 'lodash'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import apiDelete from 'lib/network/apiDelete'
import apiGet from 'lib/network/apiGet'

const AVAILABILITY_DEBOUNCE_MS = 420
const TENANT_DNS_SUFFIX_PROD = '.ajarlee.om'
const TENANT_DNS_SUFFIX_UAT = '-uat.ajarlee.om'
/** Local admin build: preview matches `slug.localhost` (no Route 53 in API dev). */
const TENANT_DNS_SUFFIX_DEV = '.localhost'

const env = process.env.REACT_APP_ENV
/** Production: `.ajarlee.om` — development: `.localhost` — otherwise: UAT `-uat.ajarlee.om`. */
const TENANT_DNS_SUFFIX = env === 'production' ? TENANT_DNS_SUFFIX_PROD : env === 'development' ? TENANT_DNS_SUFFIX_DEV : TENANT_DNS_SUFFIX_UAT

const CompanyDnsTab = ({ companyId, companyData, refetch }) => {
  const { t } = useTranslation()
  const [dnsSlug, setDnsSlug] = useState('')
  const [slugEditMode, setSlugEditMode] = useState(false)
  const [dnsSaving, setDnsSaving] = useState(false)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityInfo, setAvailabilityInfo] = useState(null)
  const availabilitySeqRef = useRef(0)

  const storedSlug = trim(companyData?.tenantSlug || '')
  const hasStoredSlug = Boolean(storedSlug)

  useEffect(() => {
    if (!hasStoredSlug) {
      setSlugEditMode(false)
    }
  }, [hasStoredSlug])

  useEffect(() => {
    if (!slugEditMode) {
      setDnsSlug(companyData?.tenantSlug || '')
    }
  }, [companyData?.tenantSlug, slugEditMode])

  const isDnsProvisioning = companyData?.dnsStatus === 'pending' || companyData?.dnsStatus === 'provisioning'

  const previewHost = useMemo(() => {
    const slug = trim(dnsSlug)
    if (!slug) return `${t('companyProfile.dnsPreviewPlaceholder')}${TENANT_DNS_SUFFIX}`
    return `${slug}${TENANT_DNS_SUFFIX}`
  }, [dnsSlug, t])

  useEffect(() => {
    const s = trim(dnsSlug)
    if (!s) {
      setAvailabilityInfo(null)
      setAvailabilityLoading(false)
      return undefined
    }

    const runId = ++availabilitySeqRef.current
    setAvailabilityLoading(true)
    setAvailabilityInfo(null)

    const timer = setTimeout(async () => {
      const res = await apiGet(`company/${companyId}/tenant-dns/availability`, { slug: s }, false)
      if (runId !== availabilitySeqRef.current) return
      setAvailabilityLoading(false)
      if (res.success && res.data && isObject(res.data)) {
        setAvailabilityInfo(res.data)
      } else {
        setAvailabilityInfo(null)
      }
    }, AVAILABILITY_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [dnsSlug, companyId])

  const slugMatchesCurrent = Boolean(companyData?.tenantSlug && trim(dnsSlug) === trim(companyData.tenantSlug))

  const availabilityMessage = useMemo(() => {
    if (availabilityLoading) return { tone: 'muted', text: t('companyProfile.dnsAvailabilityChecking') }
    if (!trim(dnsSlug)) return null
    if (slugMatchesCurrent && availabilityInfo?.available === true) {
      return { tone: 'current', text: t('companyProfile.dnsSlugCurrent') }
    }
    if (!availabilityInfo) return null
    if (availabilityInfo.reason === 'EMPTY') return null
    if (availabilityInfo.available === true) {
      return { tone: 'ok', text: t('companyProfile.dnsAvailabilityAvailable') }
    }
    if (availabilityInfo.reason === 'SLUG_TAKEN') {
      return { tone: 'bad', text: t('companyProfile.dnsAvailabilityTaken') }
    }
    if (availabilityInfo.reason === 'RESERVED_SLUG') {
      return { tone: 'warn', text: t('companyProfile.dnsAvailabilityReserved') }
    }
    if (availabilityInfo.reason === 'INVALID_SLUG') {
      return { tone: 'warn', text: t('companyProfile.dnsAvailabilityInvalid') }
    }
    if (availabilityInfo.reason === 'TENANT_DNS_NOT_CONFIGURED') {
      return { tone: 'warn', text: t('companyProfile.dnsAvailabilityNotConfigured') }
    }
    return null
  }, [availabilityInfo, availabilityLoading, dnsSlug, slugMatchesCurrent, t])

  const slugDiffersFromStored = trim(dnsSlug) !== storedSlug
  const canRetryProvisionAfterFailure = companyData?.dnsStatus === 'failed' && Boolean(trim(dnsSlug)) && trim(dnsSlug) === storedSlug

  const canProvision =
    Boolean(trim(dnsSlug)) &&
    !dnsSaving &&
    !availabilityLoading &&
    !isDnsProvisioning &&
    availabilityInfo?.available === true &&
    (slugDiffersFromStored || canRetryProvisionAfterFailure)

  const slugInputDisabled = isDnsProvisioning || (hasStoredSlug && !slugEditMode)

  const runProvisionDns = async () => {
    setDnsSaving(true)
    try {
      const res = await apiPost(`company/${companyId}/tenant-dns`, { slug: trim(dnsSlug) }, 0, false)
      if (res.success) {
        notify.success(t('companyProfile.dnsSaved'))
        setSlugEditMode(false)
        refetch?.()
      } else if (res.message === 'DNS_OLD_RECORD_DELETE_FAILED') {
        notify.error(t('companyProfile.dnsOldRecordDeleteFailed'))
      } else {
        notify.error(res.message, null, res.err)
      }
    } finally {
      setDnsSaving(false)
    }
  }

  const handleProvisionDnsClick = () => {
    notify.alertConfirm({
      title: t('companyProfile.dnsConfirmProvisionTitle'),
      message: t('companyProfile.dnsConfirmProvisionMessage'),
      onConfirm: async () => {
        await runProvisionDns()
      },
      t,
      dangerText: t('companyProfile.dnsConfirmProvisionConfirm'),
    })
  }

  const runRemoveDns = async () => {
    setDnsSaving(true)
    try {
      const res = await apiDelete(`company/${companyId}/tenant-dns`)
      if (res.success) {
        notify.success(t('companyProfile.dnsRemoved'))
        setSlugEditMode(false)
        refetch?.()
      }
    } finally {
      setDnsSaving(false)
    }
  }

  const handleRemoveDnsClick = () => {
    notify.deleteConfirm({
      title: t('companyProfile.dnsConfirmRemoveTitle'),
      message: t('companyProfile.dnsConfirmRemoveMessage'),
      onConfirm: async () => {
        await runRemoveDns()
      },
      t,
    })
  }

  const handleCancelSlugEdit = () => {
    setDnsSlug(companyData?.tenantSlug || '')
    setSlugEditMode(false)
  }

  return (
    <div className="space-y-8 py-8 pb-10 border-t border-gray-200">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-6 shadow-[0_1px_0_0_rgba(15,23,42,0.04)] sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-indigo-400/15 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-12 -left-10 h-40 w-40 rounded-full bg-violet-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-600/90">{t('companyProfile.tabs.dnsManagement')}</p>
          <h3 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{t('companyProfile.dnsTabIntroTitle')}</h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{t('companyProfile.dnsTabIntroBody')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{t('companyProfile.dnsStatus')}</p>
          <p className="mt-1.5 text-sm font-semibold text-slate-900">{companyData?.dnsStatus || '—'}</p>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm sm:col-span-2 lg:col-span-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{t('companyProfile.tenantHost')}</p>
          <p className="mt-1.5 break-all font-mono text-sm font-medium text-slate-800">{companyData?.tenantHost || '—'}</p>
        </div>
        {companyData?.dnsLastError && (
          <div className="rounded-xl border border-red-200/90 bg-red-50/80 p-4 shadow-sm sm:col-span-2 lg:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700">{t('companyProfile.dnsLastError')}</p>
            <p className="mt-1.5 text-sm text-red-800">{companyData.dnsLastError}</p>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <label className="block text-sm font-semibold text-slate-900" htmlFor="company-dns-slug">
            {t('companyProfile.tenantSlug')}
          </label>
          {hasStoredSlug && !isDnsProvisioning && (
            <div className="flex w-full min-w-0 justify-end gap-2 sm:w-auto sm:justify-start">
              {!slugEditMode ? (
                <button
                  type="button"
                  onClick={() => setSlugEditMode(true)}
                  className="text-sm font-medium text-dark-600 hover:text-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1 rounded"
                >
                  {t('companyProfile.dnsEditSlug')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCancelSlugEdit}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1 rounded"
                >
                  {t('companyProfile.dnsCancelEditSlug')}
                </button>
              )}
            </div>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{t('companyProfile.dnsFieldShortHint')}</p>

        <div className="mt-4 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch" dir="ltr">
          <div
            className={`flex min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200/90 bg-white shadow-inner ring-1 ring-slate-100 ${
              slugInputDisabled ? 'opacity-90' : 'focus-within:border-dark-500 focus-within:ring-2 focus-within:ring-dark-500/20'
            }`}
          >
            <input
              id="company-dns-slug"
              type="text"
              autoComplete="off"
              spellCheck="false"
              disabled={slugInputDisabled}
              className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50/80"
              value={dnsSlug}
              onChange={e => setDnsSlug(toLower(e.target.value).replace(/[^a-z0-9-]/g, ''))}
              placeholder="acme"
            />
            <span className="inline-flex shrink-0 select-none items-center border-l border-slate-200/90 bg-slate-50 px-2.5 py-2.5 font-mono text-xs text-slate-600 sm:px-3 sm:text-sm">
              {TENANT_DNS_SUFFIX}
            </span>
          </div>
        </div>

        {availabilityMessage && (
          <p
            className={`mt-3 text-sm ${
              availabilityMessage.tone === 'ok'
                ? 'font-medium text-emerald-700'
                : availabilityMessage.tone === 'current'
                ? 'font-medium text-sky-800'
                : availabilityMessage.tone === 'bad'
                ? 'font-medium text-red-700'
                : availabilityMessage.tone === 'warn'
                ? 'font-medium text-amber-800'
                : 'text-slate-500'
            }`}
            role="status"
            aria-live="polite"
          >
            {availabilityMessage.text}
          </p>
        )}

        <div className="mt-5 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-2.5" dir="ltr">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{t('companyProfile.dnsUrlPreview')}</p>
          <p className={`mt-1 break-all font-mono text-sm ${trim(dnsSlug) ? 'text-slate-900' : 'text-slate-500'}`}>https://{previewHost}</p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-end border-t border-slate-200/80 pt-5">
          <button
            type="button"
            onClick={handleRemoveDnsClick}
            disabled={dnsSaving || !companyData?.tenantSlug}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('companyProfile.removeDns')}
          </button>
          <button
            type="button"
            onClick={handleProvisionDnsClick}
            disabled={!canProvision}
            className="inline-flex items-center rounded-md border border-transparent bg-dark-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {dnsSaving ? t('companyProfile.saving') : t('companyProfile.provisionDns')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CompanyDnsTab
